import { ReactElement, useEffect, useState } from 'react'
import { ContextProps, createContext } from '../common/contextHelper'
import { UnitConversions, UserUnitSettings } from '../common/constants'
import { api } from '../services'
import { guestApi } from '../services/guestApi'
import { Devices } from '../common/enums'
import { UNIT_CONVERSIONS } from '../common/unitConversions'
import { useAuthContext } from './AuthContext'
import { useAppContext } from './AppContext'
import { deviceCache } from '../utils/deviceCache'
import { authService } from '../services/auth'

interface UserContextState {
  isLoading: boolean
  devices: Devices
  selectedDevice: string
  userUnitSettings: UserUnitSettings
  unitConversions: UnitConversions
  setUserUnitSettings: (userUnitSettings: UserUnitSettings) => void
  selectDevice: (sigfoxId: string) => void
  clearDevices: () => void
  selectDeviceLocally: (sigfoxId: string) => void
  isAdminAllDevices: boolean
  isPaginating: boolean
  loadedCount: number
  totalCount: number | null
}

const [Provider, useAccountContext] = createContext<UserContextState>(module.filename)

export { useAccountContext }

export function AccountProvider({ children }: ContextProps): ReactElement {
  const { email, isAuthorized } = useAuthContext()
  const { showCharts, setShowCharts } = useAppContext()

  const [devices, setDevices] = useState<Devices>({})
  const [isLoading, setIsLoading] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState('')
  const [userUnitSettings, setUserUnitSettings] = useState<UserUnitSettings>({})
  const [isAdminAllDevices, setIsAdminAllDevices] = useState(false)
  const [isPaginating, setIsPaginating] = useState(false)
  const [loadedCount, setLoadedCount] = useState(0)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const unitConversions = UNIT_CONVERSIONS

  const ADMIN_PAGE_LIMIT = 100

  useEffect(() => {
    // Only sync when both email is set AND user is authorized
    // This prevents race conditions during login where API calls could fail
    // before auth state is fully established
    if (email && isAuthorized) {
      sync()

      return () => {
        setIsLoading(false)
      }
    } else if (!email || !isAuthorized) {
      // Clear data when logged out or not yet authorized
      setDevices({})
      setShowCharts(false)
      setSelectedDevice('')
      setIsAdminAllDevices(false)
      setIsPaginating(false)
      setLoadedCount(0)
      setTotalCount(null)
    }

    async function sync() {
      try {
        // Skip API calls during registration flow to prevent race conditions
        // These pages handle their own API calls and user creation
        const currentPath = window.location.pathname
        const isRegistrationFlow =
          currentPath === '/registration/complete' ||
          currentPath === '/auth/callback' ||
          currentPath === '/register' ||
          currentPath === '/reset-password' ||
          currentPath === '/forgot-password'

        if (isRegistrationFlow) {
          return
        }

        // Check if we have a valid token before proceeding
        const token = localStorage.getItem('token')
        if (!token || token === 'undefined' || token === 'null') {
          console.warn('⚠️ AccountContext: Cannot fetch devices - no valid authentication token')
          return
        }

        if (email) {
          setIsLoading(true)

          // Load cached devices first for instant render
          const cachedDevices = await deviceCache.getDevices(email)
          if (cachedDevices && cachedDevices.length > 0) {
            const cachedDevicesMap = cachedDevices.reduce((acc: Devices, device: any) => {
              acc[device.sigfoxId] = device
              return acc
            }, {} as Devices)
            setDevices(cachedDevicesMap)
          }

          // Fetch user unit settings first
          await syncUserUnitSettings()

          // Resolve role from the canonical backend source (legacy tuser table).
          // Supabase user_metadata.role can drift (seeded users, manual DB edits)
          // and must not be trusted alone — using it caused partners to enter
          // the admin pagination path and never terminate the loop.
          let userRole: 'ADMIN' | 'PARTNER' | 'CUSTOMER' | 'GUEST' | undefined
          try {
            const me = await api.getMe()
            userRole = me.role.toUpperCase() as typeof userRole
          } catch {
            const session = await authService.getSession()
            userRole =
              (session?.user?.user_metadata?.role?.toUpperCase() ||
                session?.user?.app_metadata?.role?.toUpperCase()) as typeof userRole
          }
          const isGuest = userRole === 'GUEST'
          const isAdmin = userRole === 'ADMIN'
          setIsAdminAllDevices(isAdmin)

          if (isGuest) {
            // Guest users use a different API endpoint
            const result = await guestApi.getSharedDevices()

            if (result.devices && Array.isArray(result.devices)) {
              // Transform guest devices to match the expected Devices format
              // Guest devices now include all DeviceInfo fields from backend
              const devicesMap = result.devices.reduce((acc: Devices, device: any) => {
                acc[device.wisolId] = {
                  // All DeviceInfo fields from tdevice and tdevicecfg
                  deviceId: device.deviceId,
                  sigfoxId: device.sigfoxId || device.wisolId,
                  wisolId: device.wisolId,
                  wisolPac: device.wisolPac,
                  deviceName: device.deviceName || device.description,
                  description: device.description,
                  serialNumber: device.serialNumber,
                  hardwareNumber: device.hardwareNumber,
                  sigfoxReg: device.sigfoxReg,
                  deviceProductId: device.deviceProductId,
                  dateCode: device.dateCode,
                  locationId: device.locationId,
                  messageSequenceNumberTimestampId: device.messageSequenceNumberTimestampId,
                  deviceConfigId: device.deviceConfigId,
                  deviceConfigFlags: device.deviceConfigFlags,
                  sequenceNumberTimestampFrequency: device.sequenceNumberTimestampFrequency,
                  scheduledMessagesFrequency: device.scheduledMessagesFrequency,
                  firmwareVersion: device.firmwareVersion,
                  coordinateType: device.coordinateType,
                  coordinateNSValue: device.coordinateNSValue,
                  coordinateEWValue: device.coordinateEWValue,
                  coordinateZValue: device.coordinateZValue,
                  coordinateGridzone: device.coordinateGridzone,
                  siteName: device.siteName,
                  siteCode: device.siteCode,
                  deviceApp: device.deviceApp,
                  // Guest-specific fields
                  accessLevel: device.accessLevel,
                  grantedBy: device.grantedBy,
                  grantedAt: device.grantedAt,
                } as any
                return acc
              }, {} as Devices)
              setDevices(devicesMap)
              // Note: Skipping cache for guest devices as they have different structure
            } else {
              console.warn('⚠️ AccountContext: Guest API returned invalid devices data:', result)
              setDevices({})
            }
          } else if (isAdmin) {
            // Admin sees ALL devices across every account. Fetch the first page
            // immediately for fast first paint, then stream the remaining pages
            // in the background and merge by sigfoxId. Cache is namespaced with
            // ":admin" so a future role downgrade does not surface cross-tenant
            // data from a stale cache.
            const adminCacheKey = `${email}:admin`

            const cachedAdminDevices = await deviceCache.getDevices(adminCacheKey)
            const cachedMap: Devices =
              cachedAdminDevices && cachedAdminDevices.length > 0
                ? cachedAdminDevices.reduce((acc: Devices, device: any) => {
                    acc[device.sigfoxId] = device
                    return acc
                  }, {} as Devices)
                : ({} as Devices)

            if (Object.keys(cachedMap).length > 0) {
              setDevices(cachedMap)
            }

            const firstPage = await api.getDevicesPage(1, ADMIN_PAGE_LIMIT, true, true)
            if (!firstPage.devices || !Array.isArray(firstPage.devices)) {
              console.warn('⚠️ AccountContext: Admin API returned invalid devices data:', firstPage)
              setDevices({})
              return
            }

            // Seeded from the cache rather than starting empty. Publishing only
            // page 1 used to drop every cached device that lives on a later
            // page, so anything the user was already looking at vanished from
            // `devices` and reappeared seconds later when its page arrived —
            // on the charts page that unmounted and rebuilt every chart, which
            // is what made them flicker on refresh. Fresh pages still overwrite
            // the cached copies as they land.
            const merged: Devices = { ...cachedMap }
            firstPage.devices.forEach((device: any) => {
              merged[device.sigfoxId] = device
            })
            setDevices({ ...merged })
            setLoadedCount(firstPage.devices.length)
            setTotalCount(typeof firstPage.total === 'number' ? firstPage.total : null)

            if (firstPage.hasMore) {
              setIsPaginating(true)
              ;(async () => {
                try {
                  let page = 2
                  // Loop until the server says no more pages, or we've collected
                  // at least `total` devices (defensive cap).
                  while (true) {
                    const next = await api.getDevicesPage(page, ADMIN_PAGE_LIMIT, true, true)
                    if (!next.devices || next.devices.length === 0) break
                    // Bail out if the server response shape is the non-admin
                    // one (no hasMore field) — happens when the JWT says admin
                    // but the DB role is not. Without this guard, the loop
                    // refetches the same page until the token expires.
                    if (typeof next.hasMore !== 'boolean') break
                    for (const device of next.devices) {
                      merged[device.sigfoxId] = device
                    }
                    setDevices({ ...merged })
                    setLoadedCount(Object.keys(merged).length)
                    if (typeof next.total === 'number') setTotalCount(next.total)
                    if (!next.hasMore) break
                    page += 1
                  }
                  await deviceCache.setDevices(adminCacheKey, Object.values(merged))
                } catch (err) {
                  console.error('❌ AccountContext: admin pagination error:', err)
                } finally {
                  setIsPaginating(false)
                }
              })()
            } else {
              await deviceCache.setDevices(adminCacheKey, Object.values(merged))
            }
          } else {
            // Regular users use the standard devices API
            const result = await api.getDevices(true, true)

            if (result.devices && Array.isArray(result.devices)) {
              // Cache the fresh data for next time
              await deviceCache.setDevices(email, result.devices)

              const devicesMap = result.devices.reduce((acc: Devices, device: any) => {
                acc[device.sigfoxId] = device
                return acc
              }, {} as Devices)
              setDevices(devicesMap)
            } else {
              console.warn('⚠️ AccountContext: API returned invalid devices data:', result)
              setDevices({})
            }
          }
        }
      } catch (error: any) {
        console.error('❌ AccountContext: Error fetching devices:', error)
        console.error('Error details:', error.message, error.code, error.statusCode)
        // Keep cached devices if API fails, don't clear them
        // This allows offline functionality
      } finally {
        setIsLoading(false)
      }
    }
  }, [email, isAuthorized]) // eslint-disable-line react-hooks/exhaustive-deps

  function selectDevice(sigfoxId: string) {
    setSelectedDevice(sigfoxId)
  }

  async function syncUserUnitSettings(): Promise<UserUnitSettings> {
    const result = await api.getUserUnits()
    setUserUnitSettings(result.data.userUnitSettings)
    return result.data.userUnitSettings
  }

  function selectDeviceLocally(sigfoxId: string) {
    // worker.sendRequest('test', { test: 'test' })
    if (sigfoxId === selectedDevice && sigfoxId !== '') {
      if (showCharts) {
        selectDevice('')
      }
      setShowCharts(!showCharts)
    } else {
      selectDevice(sigfoxId)
    }
  }

  return (
    <Provider
      value={{
        isLoading,
        devices,
        selectedDevice,
        userUnitSettings,
        unitConversions,
        setUserUnitSettings,
        selectDevice,
        clearDevices: () => setDevices({}),
        selectDeviceLocally,
        isAdminAllDevices,
        isPaginating,
        loadedCount,
        totalCount,
      }}
    >
      {children}
    </Provider>
  )
}
